import {
    Box,
    Button,
    Paper,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    type ToggleButtonGroupProps,
    Typography,
} from '@mui/material'
import { type GetCandidateProfileForRecruiterQuery } from '__generated__/graphql'
import { type Dispatch, type FC, type SetStateAction } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useRosterSearch } from 'recruiter/hooks'
import { RosterFilters } from 'recruiter/pages/Research/Filters'
import {
    type CandidateRouteParams,
    recruiterRouteConfig,
} from 'recruiter/routes'
import { getPxAsRem } from 'shared/utils'
import { type CandidateRosterTableViewId } from '..'
import { CandidateRostersSansCandidateTable } from './CandidateRostersSansCandidateTable'
import { CandidateRostersTable } from './CandidateRostersTable'

interface CandidateRostersProps {
    data: GetCandidateProfileForRecruiterQuery
    tableView: CandidateRosterTableViewId
    setTableView: Dispatch<SetStateAction<CandidateRosterTableViewId>>
}

const CandidateRosters: FC<CandidateRostersProps> = ({
    data,
    tableView,
    setTableView,
}) => {
    const { candidateId } = useParams<CandidateRouteParams>()
    const [searchParams] = useSearchParams()
    const { tableState, setTableState } = useRosterSearch()
    const navigate = useNavigate()

    const handleCandidateViewChange: ToggleButtonGroupProps['onChange'] = (
        event,
        newValue: CandidateRosterTableViewId
    ) => {
        if (newValue === null) return
        setTableView(newValue)
        navigate(
            `${recruiterRouteConfig.candidates.path}/${candidateId}/overview/${newValue}?${searchParams}`,
            { replace: true }
        )
    }

    const verticalSpacingInPixels = 24

    return (
        <Stack
            component={Paper}
            borderRadius="8px"
            direction="row"
            overflow="hidden"
            sx={{
                // Set a max height for the panel view that fits itself to the
                // available area for the main content, maximizing the size
                // of the visible table at all browser sizes.
                '@container main (min-width: 0px)': {
                    height: `calc(80cqb - ${getPxAsRem(verticalSpacingInPixels * 2)})`,
                },
            }}
        >
            <Stack
                flex={`0 0 ${getPxAsRem(340)}`}
                maxWidth={getPxAsRem(340)}
                borderRight={1}
                borderColor="divider"
            >
                <Stack
                    direction="row"
                    borderBottom={1}
                    borderColor="divider"
                    padding={getPxAsRem(16)}
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Typography variant="h4">Filters</Typography>

                    <Button
                        onClick={() => setTableState({ filters: [] })}
                        variant="text"
                        size="small"
                        disabled={tableState.filters.length === 0}
                    >
                        Clear
                    </Button>
                </Stack>

                <RosterFilters />
            </Stack>

            <Stack
                flex="1"
                gap={getPxAsRem(16)}
                padding={getPxAsRem(16)}
                overflow="hidden"
            >
                <Box borderColor="divider">
                    <Typography variant="h4">Searches</Typography>
                </Box>
                <Stack direction="row" gap={2}>
                    <ToggleButtonGroup
                        value={tableView}
                        size="small"
                        exclusive
                        onChange={handleCandidateViewChange}
                        aria-label="Searches to view"
                        color="primary"
                    >
                        <ToggleButton
                            aria-label="On these searches"
                            sx={{ paddingX: getPxAsRem(12) }}
                            value="on"
                        >
                            On These Searches
                        </ToggleButton>

                        <ToggleButton
                            aria-label="All searches"
                            sx={{ paddingX: getPxAsRem(12) }}
                            value="all"
                        >
                            All Searches
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Stack>

                {tableView === 'on' && <CandidateRostersTable />}

                {tableView === 'all' && <CandidateRostersSansCandidateTable />}
            </Stack>
        </Stack>
    )
}

export default CandidateRosters
